import { createContext, useContext, useEffect, useState } from "react";
import { LocalStorageKeys, useLocalStorage } from "../../util/localStorage";

type PersistentAgentViewPreferences = {
  showNewestMessagesFirst: boolean;
  showCompactStateContent: boolean;
};

export type AgentViewPreferences = {
  showNewestMessagesFirst: boolean;
  setShowNewestMessagesFirst: (showNewestMessagesFirst: boolean) => void;
  getComponentTextFilter: (componentId?: string) => string;
  setComponentTextFilter: (componentId: string, textFilter: string) => void;
  setMessageStreamTextFilter: (textFilter: string) => void;
  messageStreamTextFilter: string;
  showCompactStateContent: boolean;
  setShowCompactStateContent: (showCompactStateContent: boolean) => void;
};

const getPersistentAgentViewPreferences = (
  preferences: AgentViewPreferences
): PersistentAgentViewPreferences => {
  return {
    showNewestMessagesFirst: preferences.showNewestMessagesFirst,
    showCompactStateContent: preferences.showCompactStateContent,
  };
};

const AgentViewPreferenceContext = createContext<AgentViewPreferences | null>(
  null
);

export const useAgentViewPreferences = (): AgentViewPreferences => {
  const context = useContext(AgentViewPreferenceContext);
  if (!context) {
    throw new Error(
      "useAgentViewPreferences must be used within a AgentViewPreferencesProvider"
    );
  }
  return context;
};

export const AgentViewPreferencesProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const localStorage = useLocalStorage<PersistentAgentViewPreferences>();
  const persistedPreferences = localStorage.getItem(
    LocalStorageKeys.AGENT_VIEW_PREFERENCES
  );
  const getPersistedPreference = (
    preference: keyof PersistentAgentViewPreferences,
    defaultValue: boolean
  ) => {
    return persistedPreferences?.[preference] ?? defaultValue;
  };

  const [showNewestMessagesFirst, setShowNewestMessagesFirst] = useState(
    getPersistedPreference("showNewestMessagesFirst", true)
  );
  const [showCompactStateContent, setShowCompactStateContent] = useState(
    getPersistedPreference("showCompactStateContent", false)
  );
  const [componentTextFilters, setComponentTextFilters] = useState<
    Record<string, string>
  >({});
  const [messageStreamTextFilter, _setMessageStreamTextFilter] = useState("");

  const setComponentTextFilter = (componentId: string, textFilter: string) => {
    setComponentTextFilters((prev) => ({
      ...prev,
      [componentId]: textFilter,
    }));
  };

  const setMessageStreamTextFilter = (textFilter: string) => {
    _setMessageStreamTextFilter(textFilter);
  };

  const getComponentTextFilter = (componentId?: string) => {
    return componentId ? (componentTextFilters[componentId] ?? "") : "";
  };

  const preferences = {
    showNewestMessagesFirst,
    setShowNewestMessagesFirst,
    getComponentTextFilter,
    setComponentTextFilter,
    showCompactStateContent,
    setShowCompactStateContent,
    messageStreamTextFilter,
    setMessageStreamTextFilter,
  };

  const persistentPreferences = getPersistentAgentViewPreferences(preferences);

  useEffect(() => {
    localStorage.setItem(
      LocalStorageKeys.AGENT_VIEW_PREFERENCES,
      persistentPreferences
    );
  }, [JSON.stringify(persistentPreferences)]);

  return (
    <AgentViewPreferenceContext.Provider value={preferences}>
      {children}
    </AgentViewPreferenceContext.Provider>
  );
};
